import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  Unsubscribe,
  query,
  where,
  or,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { BucketItem, Location } from '../types';

const COLLECTIONS = {
  ITEMS: 'bucketItems',  // Now at root level, not nested under users
  LOCATIONS: 'customLocations',
} as const;

/**
 * Remove undefined values from object (Firestore doesn't allow undefined)
 */
const cleanUndefined = <T extends Record<string, any>>(obj: T): T => {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        cleaned[key] = value;
      } else if (value && typeof value === 'object' && value.constructor === Object) {
        cleaned[key] = cleanUndefined(value);
      } else {
        cleaned[key] = value;
      }
    }
  }
  return cleaned as T;
};

/**
 * Firestore service for bucket list data
 * All data is scoped to the authenticated user
 */

export const firestoreService = {
  /**
   * Get user's collection reference (for customLocations only now)
   */
  getUserCollection: (userId: string, collectionName: string) => {
    return collection(db, 'users', userId, collectionName);
  },

  /**
   * Save a bucket list item to root-level collection
   */
  saveItem: async (item: BucketItem): Promise<void> => {
    const itemsRef = collection(db, COLLECTIONS.ITEMS);
    // Clean undefined values before saving
    const cleanItem = cleanUndefined(item);
    await setDoc(doc(itemsRef, item.id), cleanItem);
  },

  /**
   * Get all bucket list items for a user
   * Includes items they own AND items shared with them
   */
  getItems: async (userId: string): Promise<BucketItem[]> => {
    const itemsRef = collection(db, COLLECTIONS.ITEMS);
    
    // Query for items where user is owner OR user is in sharedWith array
    const q = query(
      itemsRef,
      or(
        where('ownerId', '==', userId),
        where('sharedWith', 'array-contains', userId)
      )
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as BucketItem);
  },

  /**
   * Delete a bucket list item from root-level collection
   */
  deleteItem: async (_userId: string, itemId: string): Promise<void> => {
    const itemRef = doc(db, COLLECTIONS.ITEMS, itemId);
    await deleteDoc(itemRef);
  },

  /**
   * Subscribe to real-time updates for user's items (owned + shared)
   */
  subscribeToItems: (
    userId: string,
    callback: (items: BucketItem[]) => void
  ): Unsubscribe => {
    const itemsRef = collection(db, COLLECTIONS.ITEMS);
    
    // Query for items where user is owner OR user is in sharedWith array
    const q = query(
      itemsRef,
      or(
        where('ownerId', '==', userId),
        where('sharedWith', 'array-contains', userId)
      )
    );
    
    return onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map((doc) => doc.data() as BucketItem);
      callback(items);
    });
  },

  /**
   * Save a custom location
   */
  saveCustomLocation: async (userId: string, location: Location): Promise<void> => {
    const locationsRef = firestoreService.getUserCollection(userId, COLLECTIONS.LOCATIONS);
    await setDoc(doc(locationsRef, location.id), location);
  },

  /**
   * Get all custom locations for a user
   */
  getCustomLocations: async (userId: string): Promise<Location[]> => {
    const locationsRef = firestoreService.getUserCollection(userId, COLLECTIONS.LOCATIONS);
    const snapshot = await getDocs(locationsRef);
    return snapshot.docs.map((doc) => doc.data() as Location);
  },

  /**
   * Batch save multiple items (for initial sync/migration)
   */
  batchSaveItems: async (items: BucketItem[]): Promise<void> => {
    const promises = items.map((item) => firestoreService.saveItem(item));
    await Promise.all(promises);
  },

  /**
   * Batch save multiple locations
   */
  batchSaveLocations: async (userId: string, locations: Location[]): Promise<void> => {
    const promises = locations.map((location) =>
      firestoreService.saveCustomLocation(userId, location)
    );
    await Promise.all(promises);
  },
};
